import { Alert, Button, Flex, Input } from '@conteg/ui';
import { useTranslation } from 'react-i18next';
import { usePackageAuditMessagesQuery } from 'types/generated/graphql';

import { useDebounce } from '../../../utils/hooks/use-debounce';
import AuditViewFilterHeader from '../components/audit-view-filter-header';
import { AuditViewLayout } from '../components/audit-view-layout';
import { DateRangeFilterComponent } from '../components/date-range-filter';
import { EventTypeSelect } from '../components/event-type-select';
import { EventsTable } from '../components/events-table';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { useContentFilters } from '../use-audit-filters';
import { getDefaultDateRange } from '../utils';

/**
 * Content View
 * Primary filter: Tracking Identifier (exact match search)
 * Shows timeline of all events for the package
 */
export function ContentView() {
  const { t } = useTranslation();
  const [filters, setFilters] = useContentFilters();

  // Debounce the tracking identifier for the query
  const debouncedTrackingId = useDebounce(filters.trackingIdentifier, 500);

  // Fetch audit messages for the tracking identifier
  const {
    data: { packageAuditMessages } = {},
    isLoading,
    isError,
    error,
  } = usePackageAuditMessagesQuery(
    {
      trackingNumber: debouncedTrackingId,
      from: filters.dateFrom?.toISOString(),
      to: filters.dateTo?.toISOString(),
      pageIndex: parseInt(filters.page) - 1,
      pageSize: DEFAULT_PAGE_SIZE,
      subjects: filters.eventTypes.length > 0 ? filters.eventTypes : undefined,
      sortOrder: filters.sortOrder,
    },
    // Only fetch when tracking ID has at least 3 characters to avoid unnecessary API calls
    { enabled: !!debouncedTrackingId && debouncedTrackingId.length >= 3 }
  );

  const events = packageAuditMessages?.items ?? [];
  const pagination = {
    currentPage: (packageAuditMessages?.pageIndex ?? 0) + 1,
    totalPages: packageAuditMessages?.totalPages ?? 0,
    totalItems: packageAuditMessages?.totalCount ?? 0,
    itemsPerPage: packageAuditMessages?.pageSize ?? 50,
  };

  const hasActiveFilters =
    filters.eventTypes.length > 0 ||
    filters.dateFrom?.getTime() !== getDefaultDateRange().from.getTime() ||
    filters.dateTo?.getTime() !== getDefaultDateRange().to.getTime();

  return (
    <AuditViewLayout currentView="content">
      <AuditViewFilterHeader
        title={t('Events.Tabs.Content')}
        mainFilter={
          <Input
            value={filters.trackingIdentifier}
            placeholder={t('Events.Filter.TrackingIdentifier.Placeholder')}
            label={t('Events.Filter.TrackingIdentifier')}
            onChange={(ev) =>
              setFilters({ trackingIdentifier: ev.target.value })
            }
          />
        }
        filters={
          <>
            <DateRangeFilterComponent
              value={{ from: filters.dateFrom, to: filters.dateTo }}
              onChange={(dateRange) =>
                setFilters({
                  dateFrom: dateRange.from,
                  dateTo: dateRange.to,
                })
              }
            />
            <EventTypeSelect
              selectedEventTypes={filters.eventTypes}
              onEventTypesChange={(eventTypes) =>
                setFilters({ eventTypes, page: null })
              }
              view="content"
            />
          </>
        }
        clearFiltersButton={
          hasActiveFilters ? (
            <Button
              title={t('Events.Button.ClearFilters')}
              onClick={() => {
                setFilters((prev) => ({
                  ...prev,
                  eventTypes: null,
                  dateFrom: null,
                  dateTo: null,
                  page: null,
                }));
              }}
              variant="danger"
            />
          ) : undefined
        }
      />

      <Flex flexDirection="column" gap="2rem">
        {isError && (
          <Alert
            type="error"
            title={t('Error.GeneralErrorMessage')}
            message={t('Events.Error.LoadingData.Package')}
            error={error}
          />
        )}

        <EventsTable
          events={events}
          loading={isLoading}
          pagination={pagination}
          onPageChange={(page) => setFilters({ page: page.toString() })}
          sortOrder={filters.sortOrder}
          onSortChange={(sortOrder) => setFilters({ sortOrder })}
          view="content"
        />
      </Flex>
    </AuditViewLayout>
  );
}
